import { blobMediaUrl } from "@/lib/blob-url"
import { goKeys as k } from "@/i18n/go-keys"
import { homeKeys as hk } from "@/i18n/home-keys"
import {
  ArrowPathIcon,
  BatteryLowIcon,
  NutritionIcon,
  DropletIcon,
} from "@/components/ui/icons"
import { Heartbeat, Scales } from "@phosphor-icons/react/dist/ssr"
import {
  MetabolismOrganIcon,
  HeartOrganIcon,
  ThyroidOrganIcon,
  LiverOrganIcon,
} from "@/components/ui/organ-icons"

/* ── section data (keys live in the `go` namespace) ── */

// The six most commonly-recognised metabolism signs, from the verified copy
// spec: post-meal energy crashes, stubborn weight, tiredness after sleep,
// hunger/cravings, family history, and normal-weight-but-not-fit. What people
// actually mean by a "slow metabolism", not a clinical diagnosis.
export const SIGNS = [
  {
    titleKey: k.metabolismSign1Title,
    descKey: k.metabolismSign1Desc,
    Icon: BatteryLowIcon, // energy crashes after meals
  },
  {
    titleKey: k.metabolismSign2Title,
    descKey: k.metabolismSign2Desc,
    Icon: ArrowPathIcon, // weight that won't shift
  },
  {
    titleKey: k.metabolismSign3Title,
    descKey: k.metabolismSign3Desc,
    Icon: DropletIcon, // tired even after sleep
  },
  {
    titleKey: k.metabolismSign4Title,
    descKey: k.metabolismSign4Desc,
    Icon: NutritionIcon, // always hungry / craving sugar
  },
  {
    titleKey: k.metabolismSign5Title,
    descKey: k.metabolismSign5Desc,
    Icon: Heartbeat, // diabetes or heart disease in the family
  },
  {
    titleKey: k.metabolismSign6Title,
    descKey: k.metabolismSign6Desc,
    Icon: Scales, // healthy weight, but not feeling fit
  },
] as const

// The four groups we read, in the verified-spec order: blood sugar & insulin,
// lipids & cardiovascular, thyroid, liver & metabolic. Rendered in the
// health-risks card design (photo tile + icon, category eyebrow, title,
// description, "screens for" chips).
// TODO: metabolism imagery — tiles reuse the immunity risk photos as placeholders.
export const MARKERS = [
  {
    eyebrowKey: k.metabolismMarker1Eyebrow,
    titleKey: k.metabolismMarker1Title,
    descKey: k.metabolismMarker1Desc,
    Icon: MetabolismOrganIcon, // blood sugar & insulin
    iconClassName: "h-20 w-20", // molecule glyph is small in its viewBox — size up to match
    tileImage: "/measure-tile-blood-sugar.png", // Figma tile image, cropped to spec (node 240:874)
    chips: [k.metabolismMarker1Chip1, k.metabolismMarker1Chip2],
  },
  {
    eyebrowKey: k.metabolismMarker3Eyebrow,
    titleKey: k.metabolismMarker3Title,
    descKey: k.metabolismMarker3Desc,
    Icon: ThyroidOrganIcon, // thyroid — part of metabolism's core regulation, kept next to blood sugar
    iconClassName: "h-16 w-16",
    tileImage: "/measure-tile-thyroid.png", // Figma tile image, cropped to spec (node 240:921)
    chips: [k.metabolismMarker3Chip1, k.metabolismMarker3Chip2],
  },
  {
    eyebrowKey: k.metabolismMarker2Eyebrow,
    titleKey: k.metabolismMarker2Title,
    descKey: k.metabolismMarker2Desc,
    Icon: HeartOrganIcon, // lipids & cardiovascular — downstream/linked risk, after the core
    iconClassName: "h-16 w-16",
    tileImage: "/measure-tile-lipids.png", // Figma tile image, cropped to spec (node 240:897)
    chips: [k.metabolismMarker2Chip1, k.metabolismMarker2Chip2],
  },
  {
    eyebrowKey: k.metabolismMarker4Eyebrow,
    titleKey: k.metabolismMarker4Title,
    descKey: k.metabolismMarker4Desc,
    Icon: LiverOrganIcon, // liver & metabolic
    iconClassName: "h-16 w-16",
    tileImage: "/measure-tile-liver.png", // Figma tile image, cropped to spec (node 240:946)
    chips: [k.metabolismMarker4Chip1, k.metabolismMarker4Chip2],
  },
] as const

// Symptom → biomarker bridge (verified copy spec, 4 cross-marker stories). Each
// story shows the several markers — often across more than one group — that,
// read together, tell a story a single value would miss. `group` on each reading
// surfaces that the markers come from different groups. `lead` is the punchy
// hook; `body` the fuller explanation; `readings` the 2–5 markers we'd look at
// together. Values + badges match the spec exactly.
export const INSIGHTS_V2 = [
  {
    titleKey: k.metabolismStory1Title,
    leadKey: k.metabolismStory1Lead,
    bodyKey: k.metabolismStory1Body,
    readings: [
      {
        markerKey: k.metabolismMkFastingGlucose,
        groupKey: k.metabolismGroupBloodSugar,
        value: "5.4",
        unit: "mmol/l",
        badgeKey: k.metabolismBadgeNormal,
        tone: "green",
        zones: [
          { c: "sand", w: 22 },
          { c: "green", w: 54 },
          { c: "terra", w: 24 },
        ],
        dot: 48,
      },
      {
        markerKey: k.metabolismMkHba1c,
        groupKey: k.metabolismGroupBloodSugar,
        value: "5.9",
        unit: "%",
        badgeKey: k.metabolismBadgeIntermediate,
        tone: "sand",
        zones: [
          { c: "green", w: 50 },
          { c: "sand", w: 28 },
          { c: "terra", w: 22 },
        ],
        dot: 62,
      },
      {
        markerKey: k.metabolismMkHomaIr,
        groupKey: k.metabolismGroupBloodSugar,
        value: "2.6",
        unit: "",
        badgeKey: k.metabolismBadgeRaised,
        tone: "sand",
        zones: [
          { c: "green", w: 54 },
          { c: "sand", w: 22 },
          { c: "terra", w: 24 },
        ],
        dot: 66,
      },
    ],
  },
  {
    titleKey: k.metabolismStory2Title,
    leadKey: k.metabolismStory2Lead,
    bodyKey: k.metabolismStory2Body,
    readings: [
      {
        markerKey: k.metabolismMkTsh,
        groupKey: k.metabolismGroupThyroid,
        value: "6.2",
        unit: "mIU/l",
        badgeKey: k.metabolismBadgeHigh,
        tone: "terra",
        zones: [
          { c: "green", w: 54 },
          { c: "sand", w: 22 },
          { c: "terra", w: 24 },
        ],
        dot: 86,
      },
      {
        markerKey: k.metabolismMkFt4,
        groupKey: k.metabolismGroupThyroid,
        value: "12",
        unit: "pmol/l",
        badgeKey: k.metabolismBadgeLowNormal,
        tone: "sand",
        zones: [
          { c: "terra", w: 24 },
          { c: "sand", w: 22 },
          { c: "green", w: 54 },
        ],
        dot: 32,
      },
      {
        markerKey: k.metabolismMkHba1c,
        groupKey: k.metabolismGroupBloodSugar,
        value: "5.8",
        unit: "%",
        badgeKey: k.metabolismBadgeIntermediate,
        tone: "sand",
        zones: [
          { c: "green", w: 50 },
          { c: "sand", w: 28 },
          { c: "terra", w: 22 },
        ],
        dot: 60,
      },
    ],
  },
  {
    titleKey: k.metabolismStory3Title,
    leadKey: k.metabolismStory3Lead,
    bodyKey: k.metabolismStory3Body,
    readings: [
      {
        markerKey: k.metabolismMkAlt,
        groupKey: k.metabolismGroupLiver,
        value: "48",
        unit: "U/l",
        badgeKey: k.metabolismBadgeHigh,
        tone: "terra",
        zones: [
          { c: "green", w: 56 },
          { c: "sand", w: 22 },
          { c: "terra", w: 22 },
        ],
        dot: 80,
      },
      {
        markerKey: k.metabolismMkGgt,
        groupKey: k.metabolismGroupLiver,
        value: "62",
        unit: "U/l",
        badgeKey: k.metabolismBadgeHigh,
        tone: "terra",
        zones: [
          { c: "green", w: 54 },
          { c: "sand", w: 22 },
          { c: "terra", w: 24 },
        ],
        dot: 78,
      },
      {
        markerKey: k.metabolismMkTriglycerides,
        groupKey: k.metabolismGroupLipids,
        value: "2.0",
        unit: "mmol/l",
        badgeKey: k.metabolismBadgeHigh,
        tone: "terra",
        zones: [
          { c: "green", w: 54 },
          { c: "sand", w: 22 },
          { c: "terra", w: 24 },
        ],
        dot: 78,
      },
    ],
  },
  {
    titleKey: k.metabolismStory4Title,
    leadKey: k.metabolismStory4Lead,
    bodyKey: k.metabolismStory4Body,
    readings: [
      {
        markerKey: k.metabolismMkVisceralFat,
        groupKey: k.metabolismGroupImaging,
        value: "",
        unit: "",
        badgeKey: k.metabolismBadgeNormal,
        tone: "green",
        zones: [
          { c: "green", w: 52 },
          { c: "sand", w: 24 },
          { c: "terra", w: 24 },
        ],
        dot: 34,
      },
      {
        markerKey: k.metabolismMkLiverFat,
        groupKey: k.metabolismGroupImaging,
        value: "5",
        unit: "%",
        badgeKey: k.metabolismBadgeIntermediate,
        tone: "sand",
        zones: [
          { c: "green", w: 54 },
          { c: "sand", w: 22 },
          { c: "terra", w: 24 },
        ],
        dot: 58,
      },
      {
        // Muscle / body composition. The bar is a HEALTH axis (green = good),
        // not a value axis, so healthy muscle sits in the green band like every
        // other marker — same green-left layout, "you" in the green zone.
        markerKey: k.metabolismMkMuscle,
        groupKey: k.metabolismGroupImaging,
        value: "",
        unit: "",
        badgeKey: k.metabolismBadgeNormal,
        tone: "green",
        zones: [
          { c: "green", w: 52 },
          { c: "sand", w: 24 },
          { c: "terra", w: 24 },
        ],
        dot: 30,
      },
    ],
  },
] as const

export const PROCESS = [
  {
    n: "1",
    titleKey: k.metabolismProcessStep1Title,
    descKey: k.metabolismProcessStep1Desc,
    image: blobMediaUrl("/images/immunity/immunity-step-book-2.jpg"), // TODO: metabolism imagery
  },
  {
    n: "2",
    titleKey: k.metabolismProcessStep2Title,
    descKey: k.metabolismProcessStep2Desc,
    image: blobMediaUrl("/images/immunity/immunity-step-blood-draw.jpg"), // TODO: metabolism imagery
  },
  {
    // Optional MRI visit — the upsell arm, sequenced before the report so the
    // full-picture path reads as one flow (ported from /go/female-hormones).
    // Uses the MRI-walkthrough "scan" photo, distinct from the §7b immersive's
    // mri-suite background.
    n: "3",
    titleKey: k.metabolismProcessStep3Title,
    descKey: k.metabolismProcessStep3Desc,
    image: blobMediaUrl("/images/mri/walkthrough/step-scan.png"),
  },
  {
    n: "4",
    titleKey: k.metabolismProcessStep4Title,
    descKey: k.metabolismProcessStep4Desc,
    image: blobMediaUrl("/images/mri/walkthrough/step-report-v2.png"),
  },
] as const

export const STATS = [
  { valueKey: k.metabolismStat1Value, labelKey: k.metabolismStat1Label },
  { valueKey: k.metabolismStat2Value, labelKey: k.metabolismStat2Label },
  { valueKey: k.metabolismStat3Value, labelKey: k.metabolismStat3Label },
  { valueKey: k.metabolismStat4Value, labelKey: k.metabolismStat4Label },
] as const

// FAQ set lifted verbatim from /go/bloodtest (home namespace) so the metabolism
// LP shares the same blood-test answers + layout.
export const FAQ_KEYS = [
  { q: hk.faqQ1, a: hk.faqA1 },
  { q: hk.faqQ2, a: hk.faqA2 },
  { q: hk.faqQ4, a: hk.faqA4 },
  { q: hk.faqQ5, a: hk.faqA5 },
  { q: hk.faqQ6, a: hk.faqA6 },
  { q: hk.faqQ8, a: hk.faqA8 },
  { q: hk.faqQ9, a: hk.faqA9 },
] as const
